import React, { useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";

const ExpensesList = ({ tripId }) => {
  const [expenses, setExpenses] = useState([]);
  const [totals, setTotals] = useState({});
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ description: "", amount: "" });
  const [activeTab, setActiveTab] = useState("personal");  
  const [members, setMembers] = useState([]);
  const [sharedWith, setSharedWith] = useState([]);  
  const [currency, setCurrency] = useState("GBP");
  const [currencySymbol, setCurrencySymbol] = useState("£");
  const [currencies, setCurrencies] = useState([]);
  const [payerId, setPayerId] = useState("");
  const [needSplit, setNeedSplit] = useState(true);  

  const fetchExpenses = async () => {
    const token = localStorage.getItem("token");
    const response = await fetch(`${process.env.REACT_APP_API_URL}/expenses/split_expense/${tripId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.ok) {
      const data = await response.json();
      setExpenses(data.expenses);
      setTotals(data.totals);
    }
  };

  useEffect(() => {
    if (tripId) fetchExpenses();
	// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

	useEffect(() => {
	  const fetchCurrencies = async () => {
		try {
			// eslint-disable-next-line no-unused-vars
		  const token = localStorage.getItem("token");
		  const response = await fetch(`${process.env.REACT_APP_API_URL}/countries/countries/`);
		  if (response.ok) {
			const data = await response.json();
			//console.log("Fetched countries: ", data);

			const uniqueCurrencies = [];
			const seenCodes = new Set();

			for (const c of data) {
			  if (c.code && !seenCodes.has(c.code)) {
				seenCodes.add(c.code);
				uniqueCurrencies.push(c);
			  }
			}

			const priorityCodes = ["GBP", "EUR", "USD", "TWD", "JPY", "HKD"];

			const prioritized = uniqueCurrencies.filter(c => priorityCodes.includes(c.code));
			const others = uniqueCurrencies
			  .filter(c => !priorityCodes.includes(c.code))
			  .sort((a, b) => a.code.localeCompare(b.code));

			setCurrencies([...prioritized, ...others]);
		  } else {
			console.error("Failed to fetch currencies");
		  }
		} catch (error) {
		  console.error("Error fetching currencies", error);
		}
	  };

	  fetchCurrencies();
	  // eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);


	
  useEffect(() => {
    const fetchMembers = async () => {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/tripmembers/trip_members/${tripId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
		setMembers(data.filter((m) => m.user_id != null));
      } else {
        console.error("Unable to get member list");
      }
    };

    fetchMembers();
	// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);
  
  // 全選成員
  const selectAllMembers = () => {
    if (sharedWith.length === members.length) {
      setSharedWith([]); // 如果已經全選，則清除選擇
    } else {
      setSharedWith(members.map((member) => Number(member.user_id))); // 否則全選
    }
  };

  const handleDelete = async (expenseId) => {
	const confirmDelete = window.confirm("Are you sure you want to delete this expense? This action cannot be undone.");
	if (!confirmDelete) return;	  
    
	const token = localStorage.getItem("token");
    const response = await fetch(`${process.env.REACT_APP_API_URL}/expenses/expense/${expenseId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.ok) {
      setExpenses(expenses.filter((e) => e.expense_id !== expenseId));
    } else {
      alert("Delete failed");
    }
  };

	const handleEdit = (expense) => {
	  setEditId(expense.expense_id);
	  setEditForm({
		description: expense.description || "",
		amount: expense.amount || 0,
		currency: expense.currency || "GBP",
		currency_symbol: expense.currency_symbol || "£",
		type: expense.type || "",
		paid_date: expense.paid_date ? expense.paid_date.slice(0, 10) : "",
		payer_id: expense.payer_id || "",
		need_split: expense.is_shared_with_all || false,
	  });

	  const sharedIds = expense.shared_with || [];
	  setSharedWith(sharedIds);
	  setNeedSplit(expense.is_shared_with_all || false);
	};

  const handleEditSubmit = async () => {
    const token = localStorage.getItem("token");
	
    const payload = {
      ...editForm,
      shared_with: sharedWith,
      trip_id: tripId, // 若後端有驗證需要
	  need_split: editForm.need_split,
    };
	
    const response = await fetch(`${process.env.REACT_APP_API_URL}/expenses/expense/${editId}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
	
    if (response.ok) {
      setEditId(null);
      fetchExpenses(); // refresh
    } else {
      const errorData = await response.json();
      console.error("Update failed:", errorData);		
      alert("Update failed");
    }
  };

  return (
    <div className="container max-h-[90vh] overflow-y-auto p-4 sm:p-6 bg-white rounded-lg shadow-md">
      {/* Tab Navigation */}
      <div className="flex flex-col sm:flex-row justify-center gap-2 sm:space-x-4 mb-4">
        <button
          onClick={() => setActiveTab("personal")}
          className={`btn-full px-4 py-2 rounded ${activeTab === "personal" ? "bg-blue-500 text-white" : "bg-gray-200"}`}
        >
          My Summary
        </button>
        <button
          onClick={() => setActiveTab("group")}
          className={`btn-full px-4 py-2 rounded ${activeTab === "group" ? "bg-blue-500 text-white" : "bg-gray-200"}`}
        >
          Group Overview
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "personal" && (
        <div>
          <h2 className="text-lg font-semibold mb-2">My Personal Expenses</h2>
          <ul className="list-disc ml-5 space-y-2">
            {expenses.map((expense, index) => (
              <li key={index} className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b pb-4 gap-2 sm:gap-0">
				{editId === expense.expense_id ? (
				  <div className="w-full border rounded-lg p-4 bg-gray-100 flex flex-col gap-2">
					{/* Description */}
					<input
					  type="text"
					  value={editForm.description}
					  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
					  placeholder="Description"
					  className="form-input"
					/>

					{/* Type */}
					<select
					  value={editForm.type || ""}
					  onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
					  className="form-input"
					>
					  <option value="">Select Type</option>
					  {["Flights", "Lodging", "Transit", "Car rental", "Food", "Sightseeing", "Activities", "Shopping", "Gas", "Groceries", "Other"].map((type) => (
						<option key={type} value={type}>{type}</option>
					  ))}
					</select>

					{/* Paid Date */}
					<input
					  type="date"
					  value={editForm.paid_date || ""}
					  onChange={(e) => setEditForm({ ...editForm, paid_date: e.target.value })}
					  className="border p-2 rounded"
					/>

					{/* Amount */}
					<input
					  type="number"
					  value={editForm.amount}
					  onChange={(e) => setEditForm({ ...editForm, amount: parseFloat(e.target.value) })}
					  placeholder="Amount"
					  className="form-input"
					/>

					{/* Currency */}
					<select
					  value={editForm.currency || "GBP"}
					  onChange={(e) => {
						const selected = currencies.find(c => c.code === e.target.value);
						setEditForm({
						  ...editForm,
						  currency: selected.code,
						  currency_symbol: selected.symbol
						});
					  }}
					  className="form-input"
					>
					  {currencies.map(c => (
						<option key={c.code} value={c.code}>{c.code} ({c.symbol})</option>
					  ))}
					</select>

					{/* Payer */}
					<select
					  value={editForm.payer_id || ""}
					  onChange={(e) => setEditForm({ ...editForm, payer_id: Number(e.target.value) })}
					  className="form-input"
					>
					  <option value="">Select Payer</option>
					  {members.map((m) => (
						<option key={m.user_id} value={m.user_id}>
						  {m.username || m.name || `User ${m.user_id}`}
						</option>
					  ))}
					</select>

					{/* Need Split */}
					<div className="flex items-center">
					  <input
						type="checkbox"
						checked={editForm.need_split}
						onChange={(e) => setEditForm({ ...editForm, need_split: e.target.checked })}
						className="mr-2"
					  />
					  <label>Need to split?</label>
					</div>
					 <label className="block font-semibold mb-1 text-secondary">Select apportionment member(s)：</label>

						  {/* 全選/清除按鈕 */}
						  <div className="flex gap-2 mb-2">
							<button
							  type="button"
							  onClick={selectAllMembers}
							  className="bg-secondary text-white px-3 py-1 rounded hover:bg-green-700 text-sm"
							>
							  {sharedWith.length === members.length ? "Unselect All" : "Select All"}
							</button>
						  </div>

						  {/* 成員清單 */}
						  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2 max-h-40 overflow-y-auto">
							{members.map((member) => (
							  <div key={member.user_id} className="flex items-center space-x-2">
								<input
								  id={`member-${member.user_id}`}
								  type="checkbox"
								  checked={sharedWith.includes(Number(member.user_id))}
								  onChange={(e) => {
									const numericId = Number(member.user_id);
									if (e.target.checked) {
									  setSharedWith((prev) => {
										const updated = [...prev, numericId];
										return Array.from(new Set(updated)); // 去除重複
									  });
									} else {
									  setSharedWith((prev) => prev.filter((uid) => uid !== numericId));
									}
								  }}
								  className="cursor-pointer"
								/>
								<label htmlFor={`member-${member.user_id}`} className="cursor-pointer">
								  {member.username || member.name || `使用者 ${member.user_id}`}
								</label>
							  </div>
							))}
						  </div>
					{/* Buttons */}
					<div className="flex flex-col sm:flex-row gap-2">
					  <button
						onClick={handleEditSubmit}
						className="bg-blue-500 text-white px-3 py-1 rounded"
					  >
						Save
					  </button>
					  <button
						onClick={() => setEditId(null)}
						className="bg-gray-400 text-white px-3 py-1 rounded"
					  >
						Cancel
					  </button>
					</div>
				  </div>
				) : (
                  <>
                    <span className="text-gray-800">
                      {expense.description}：<span className="text-primary">{expense.currency_symbol}{expense.amount}</span> - My Part <span className="text-secondary">{expense.currency_symbol}{expense.share}</span>
                    </span>
                    <div className="flex gap-2">
                      <Pencil size={18} className="text-accent hover:text-primary cursor-pointer" onClick={() => handleEdit(expense)} />
                      <Trash2 size={18} className="text-red-500 hover:text-red-700 cursor-pointer" onClick={() => handleDelete(expense.expense_id)} />
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>

          {/* Personal Totals */}
          <div className="mt-4">
            {Object.entries(totals).map(([currency, amounts]) => (
              <div key={currency} className="mb-4">
                <h3 className="font-bold text-primary">{currency}</h3>
                <p>My Total: {amounts.personalTotal}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "group" && (
        <div>
          <h2 className="text-lg font-semibold mb-2">Group Expenses</h2>
          {/* Group Totals Only */}
          <div className="mt-4">
            {Object.entries(totals).map(([currency, amounts]) => (
              <div key={currency} className="mb-4">
                <h3 className="font-bold text-primary">{currency}</h3>
                <p>Group Total: {amounts.groupTotal}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesList;